import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router';
import QRCode from 'react-qr-code';

import PageShell from '@/components/PageShell';
import MeetMia from '@/components/confirmation/MeetMia';
import ShareMia from '@/components/confirmation/ShareMia';
import StoreSheet from '@/components/confirmation/StoreSheet';
import usePlatform from '@/hooks/usePlatform';
import MiaSignature from '@/components/MiaSignature';
import { DetailsDisplay } from '@/components/Details/Details';
import {
  Copyright as LandingCopyright,
  PartnerBar,
} from '@/components/LandingContext';

import { formatCurrency } from '@/utils/formatters/currency';
import { GIFTING_FAQS } from '@/content/gifting-faqs';
import { APP_SMART_LINK_URL, openBankingStatuses } from '@/constants';
import {
  useAmount,
  useChildFirstName,
  useCompleteGift,
  useContributionId,
  useGiftCompleted,
  useGiftInviteCode,
  useHydrated,
} from '@/store';

/** How long "Link copied" stands before reverting (GIFTWEB-30 AC 7). */
const COPIED_FEEDBACK_MS = 2_000;

/**
 * GIFTWEB-30 AC 4 asked for 160px or larger, and GIFTWEB-39 item 11 overrides
 * it: the code is sized to the tile, not blown up, which is what the design
 * draws. Scannability is unaffected — the smart link is short enough to encode
 * at a low version, so each module still lands on several pixels.
 */
const QR_SIZE_PX = 100;

/**
 * The end of the journey (GIFTWEB-30). Renders from what the store already
 * holds — it makes no API call of its own, so reloading cannot re-trigger
 * anything (AC 1).
 */
function Confirmation() {
  const isHydrated = useHydrated();
  const [searchParams] = useSearchParams();

  const code = useGiftInviteCode();
  const contributionId = useContributionId();
  const amount = useAmount();
  const childFirstName = useChildFirstName();
  const giftCompleted = useGiftCompleted();
  const completeGift = useCompleteGift();
  const platform = usePlatform();

  const [storeSheetOpen, setStoreSheetOpen] = useState(false);

  const paymentId = searchParams.get('payment_id') ?? '';
  const isPending = searchParams.get('status') === openBankingStatuses.pending;

  // Arriving with a payment id is the callback handing over; `giftCompleted`
  // is what survives a reload after the id has been released.
  //
  // The gift context is required on top of either. A payment id is just a URL
  // parameter — on its own it proves nothing (AC 1), and without a gift there
  // is no amount, no child and no link to copy, so the screen would render a
  // confirmation for £0 to nobody and hand out a `?code=` with nothing after
  // it.
  const hasGift = !!code && !!contributionId;
  const canRender = hasGift && (giftCompleted || !!paymentId);

  const settled = useRef(false);
  useEffect(() => {
    if (!isHydrated || !canRender || !paymentId || settled.current) return;
    settled.current = true;
    completeGift();
  }, [isHydrated, canRender, paymentId, completeGift]);

  if (!isHydrated) return null;

  if (!canRender) {
    return <Navigate to={code ? `/?code=${code}` : '/'} replace />;
  }

  const amountLabel = formatCurrency({ amount, precision: 0 });
  const child = childFirstName?.trim();
  const childPossessive = child ? `${child}'s` : 'their';

  return (
    <PageShell
      variant='split'
      className='gift-confirmation-page'
      left={
        <>
          <div className='flex justify-center'>
            <MiaSignature height={30} color='#1a201d' />
          </div>

          {isPending ? (
            <StatusCard
              tone='pending'
              eyebrow='Payment pending'
              headline='Your payment is still processing'
            />
          ) : (
            <StatusCard
              tone='confirmed'
              eyebrow='Payment confirmed'
              headline={`Your ${amountLabel} gift to ${childPossessive} future is on its way`}
            />
          )}

          <InfoCard
            title={isPending ? "We'll let you know" : 'Confirmation on its way'}
            body={
              isPending
                ? `We'll email you as soon as it completes. ${childPossessive} pot will update once it clears.`
                : `We've emailed your confirmation. ${childPossessive} pot will update soon.`
            }
          />

          <div className='hidden lg:block'>
            <PartnerBar variant='desktop' />
            <LandingCopyright marginTop={12} />
          </div>
        </>
      }
      right={
        <>
          <h1 className='sr-only lg:hidden'>Download the Mia app</h1>

          <div className='hidden text-left lg:block'>
            <h1 className='text-[22px] leading-tight font-bold tracking-[-0.015em] text-[#1a201d]'>
              Download the Mia app
            </h1>
            <p className='mt-2.5 text-[14.5px] leading-[1.55] text-[#5a635e]'>
              Your details are saved, so gifting again to {child || 'them'}{' '}
              takes just seconds.
            </p>
          </div>

          <DownloadPanel />
          <DownloadCard onOpen={() => setStoreSheetOpen(true)} />

          <SaveLinkCard code={code} />

          <MeetMia />

          <ShareMia />

          <FaqSection />

          <StoreSheet
            open={storeSheetOpen}
            onOpenChange={setStoreSheetOpen}
            platform={platform}
            childFirstName={childFirstName}
          />
        </>
      }
    />
  );
}

/** The blue "payment confirmed" block — AC 2, announced on arrival per AC 11. */
function StatusCard({
  tone,
  eyebrow,
  headline,
}: {
  tone: 'confirmed' | 'pending';
  eyebrow: string;
  headline: string;
}) {
  const confirmed = tone === 'confirmed';
  return (
    <div
      role='status'
      className={
        confirmed
          ? 'flex items-center gap-4 rounded-[20px] border border-[#a9cde3] bg-[linear-gradient(118deg,#7fb6d6_0%,#c0dbeb_58%,#d8e9f3_165%)] px-[21px] py-[19px] text-left'
          : 'flex items-center gap-4 rounded-[20px] border border-[#e8e4de] bg-[#f4f3ef] px-[21px] py-[19px] text-left'
      }
    >
      <span
        aria-hidden='true'
        className='flex size-[50px] shrink-0 items-center justify-center rounded-full bg-[rgba(18,44,61,0.12)]'
      >
        {confirmed ? (
          <svg width='25' height='25' viewBox='0 0 24 24' fill='none' stroke='#122c3d' strokeWidth='2.6' strokeLinecap='round' strokeLinejoin='round'>
            <path d='M20 6 9 17l-5-5' />
          </svg>
        ) : (
          <svg width='25' height='25' viewBox='0 0 24 24' fill='none' stroke='#122c3d' strokeWidth='2.2' strokeLinecap='round' strokeLinejoin='round'>
            <circle cx='12' cy='12' r='9' />
            <path d='M12 7v5l3 2' />
          </svg>
        )}
      </span>
      <div className='min-w-0 flex-1'>
        <div className='text-[10.5px] font-bold tracking-[0.09em] text-[#1a4763] uppercase'>
          {eyebrow}
        </div>
        <div className='mt-1 text-[22px] leading-[1.18] font-bold tracking-[-0.02em] text-[#122c3d]'>
          {headline}
        </div>
      </div>
    </div>
  );
}

function InfoCard({ title, body }: { title: string; body: string }) {
  return (
    <div className='flex items-center gap-[13px] rounded-2xl border border-[#ebe7df] bg-[#fbfaf9] px-[15px] py-[14px] text-left'>
      <span
        aria-hidden='true'
        className='flex size-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[#e7f5f0]'
      >
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#256956' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <rect x='2' y='4' width='20' height='16' rx='2' />
          <path d='m22 7-10 6L2 7' />
        </svg>
      </span>
      <div className='min-w-0 flex-1'>
        <div className='text-[14px] leading-[1.3] font-bold text-[#1a201d]'>
          {title}
        </div>
        <div className='mt-[3px] text-[12.5px] leading-[1.45] text-[#5a635e]'>
          {body}
        </div>
      </div>
    </div>
  );
}

/**
 * The QR panel. The code itself sits on white inside the green card, which is
 * what AC 10 asks for — a scanner needs the quiet zone light, not the panel.
 */
function DownloadPanel() {
  return (
    <div
      data-panel='qr'
      className='hidden items-center gap-5 rounded-[18px] bg-[#256956] p-[18px] shadow-[0_16px_34px_-20px_rgba(15,41,34,0.7)] lg:flex'
    >
      <div className='shrink-0 rounded-[13px] bg-white p-2'>
        <QRCode
          value={APP_SMART_LINK_URL}
          size={QR_SIZE_PX}
          // Surfaced for tests: what the code actually encodes (AC 5).
          data-encodes={APP_SMART_LINK_URL}
          role='img'
          aria-label='QR code to download the Mia app'
          style={{ display: 'block', height: 'auto', maxWidth: '100%' }}
        />
      </div>
      <div className='min-w-0 flex-1 text-left'>
        <p className='text-[17px] leading-[1.35] font-bold tracking-[-0.01em] text-white'>
          Download Mia to gift again or open an account for your own family
        </p>
        <p className='mt-[7px] text-[13px] leading-[1.5] text-[#c8e3d9]'>
          Or search <strong className='text-white'>Mia Wealth</strong> in the
          App Store or Google Play.
        </p>
        {/* GIFTWEB-39 item 5 — the warning follows the app download. */}
        <p className='mt-[7px] text-[13px] leading-[1.5] font-bold text-white'>
          Child's capital at risk.
        </p>
      </div>
    </div>
  );
}

/**
 * The mobile route to the app (GIFTWEB-27). Replaces the QR below the desktop
 * breakpoint, where a code the same device would have to scan is no use.
 */
function DownloadCard({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type='button'
      onClick={onOpen}
      className='flex w-full items-center gap-4 rounded-[18px] bg-[#256956] p-[17px] text-left shadow-[0_14px_30px_-16px_rgba(15,41,34,0.6)] lg:hidden'
    >
      <span
        aria-hidden='true'
        className='flex size-[42px] shrink-0 items-center justify-center rounded-xl bg-white/15'
      >
        <svg width='21' height='21' viewBox='0 0 24 24' fill='none' stroke='#fff' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M12 3v12M7 10l5 5 5-5M5 21h14' />
        </svg>
      </span>
      <span className='min-w-0 flex-1'>
        <span className='block text-base font-bold text-white'>Download Mia</span>
        <span className='mt-px block text-[12.5px] leading-[1.45] text-white/85'>
          Gift again in seconds, or open an account for your own family
        </span>
        {/* GIFTWEB-39 item 5 — the warning follows the app download. */}
        <span className='mt-1.25 block text-[12.5px] leading-[1.45] font-bold text-white'>
          Child's capital at risk.
        </span>
      </span>
      <svg width='19' height='19' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.8)' strokeWidth='2.4' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' className='shrink-0'>
        <path d='M9 6l6 6-6 6' />
      </svg>
    </button>
  );
}

function SaveLinkCard({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  const giftUrl = `${typeof location === 'undefined' ? '' : location.origin}/?code=${code}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(giftUrl);
    } catch (error) {
      console.error('Could not copy the gift link:', error);
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
  };

  return (
    <div className='flex items-center gap-[13px] rounded-2xl border border-[#e8e4de] bg-[#fbfaf9] px-[15px] py-[14px] text-left'>
      <span
        aria-hidden='true'
        className='flex size-[38px] shrink-0 items-center justify-center rounded-[11px] bg-[#e8e4de]'
      >
        <svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='#5a635e' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
          <path d='M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.8 1.7' />
          <path d='M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.8-1.7' />
        </svg>
      </span>
      <div className='min-w-0 flex-1'>
        <div className='text-[14px] leading-[1.3] font-bold text-[#1a201d]'>
          Save this link to gift again
        </div>
        <div className='mt-[3px] text-[12.5px] leading-[1.45] text-[#5a635e]'>
          This gift link stays live, so you can use it any time.
        </div>
        <button
          type='button'
          onClick={copy}
          className='mt-2 inline-flex items-center gap-[7px] text-[13px] font-bold text-[#256956] underline underline-offset-2'
        >
          {copied ? 'Link copied' : 'Copy your gift link'}
        </button>
      </div>
    </div>
  );
}

function FaqSection() {
  return (
    <section className='border-t border-[#ece9e1] pt-5'>
      <h2 className='mt-2 text-center text-[17px] font-bold text-[#1a201d]'>
        Frequently Asked Questions
      </h2>
      <div className='mt-3.5 flex flex-col gap-[9px]'>
        {GIFTING_FAQS.map((faq) => (
          <DetailsDisplay
            key={faq.id}
            detailsClasses='border border-[#eceae3]'
            summaryClasses='text-[14px] text-[#1a201d]'
            summaryContent={faq.question}
          >
            <FaqAnswer>{faq.answer}</FaqAnswer>
          </DetailsDisplay>
        ))}
      </div>
      <p className='mt-4 text-center text-[12.5px] leading-[1.5] text-[#5a635e]'>
        Please visit{' '}
        <a
          href='https://www.miawealth.co.uk'
          target='_blank'
          rel='noreferrer'
          className='font-semibold text-[#256956] underline'
        >
          www.miawealth.co.uk
        </a>{' '}
        for additional FAQs
      </p>
    </section>
  );
}

function FaqAnswer({ children }: { children: ReactNode }) {
  return (
    <div className='pt-2 text-left text-[13px] leading-[1.55] text-[#5a635e]'>
      {children}
    </div>
  );
}

export default Confirmation;
